import { HStack, Stack, Text, VStack } from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { TrackedButton } from '$/components/common/Button/TrackedButton';
import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import {
  HookFormCheckbox,
  type HookFormCheckboxProps,
} from '$/components/core/Form/HookFormCheckbox';
import { StyledLink } from '$/components/core/StyledLink';
import { useCloudMessageTranslation } from '$/hooks/useCloudMessageTranslation';
import { linkSapCxAccount } from '$/services/usecases/authentication';

export type LinkingData = {
  email: string;
  password: string;
  acceptedTerms: boolean;
};

const MigrationCheckbox = (props: HookFormCheckboxProps<LinkingData>) => (
  <HookFormCheckbox<LinkingData> {...props} />
);

export const SapCxLinkingForm = () => {
  const { t } = useTranslation();

  const formMethods = useForm<LinkingData>({
    mode: 'onTouched',
    delayError: 100,
  });

  const [error, setError] = useState<string | null>(null);

  const { cloudMessageTranslation } = useCloudMessageTranslation();

  const setAuthModalType = useAuthenticationStore.useSetModalType();
  const migrationPassword = useAuthenticationStore.useMigrationPassword();
  const email = useAuthenticationStore.usePendingAuthEmail();

  const { mutate: onMigrate, isPending } = useMutation({
    mutationFn: linkSapCxAccount,
    onSuccess: async (data) => {
      const { response, isSuccessful } = data;

      if (isSuccessful) {
        setAuthModalType('sapCxLinkingSuccess');
      } else {
        setError(cloudMessageTranslation(response.message));
      }
    },
    onError: () => {
      setError(cloudMessageTranslation('genericError'));
    },
  });

  return (
    <FormProvider {...formMethods}>
      <VStack
        as='form'
        gap='10'
        onSubmit={formMethods.handleSubmit((data) =>
          onMigrate({
            ...data,
            password: migrationPassword!,
            email,
          }),
        )}
      >
        <Stack gap='6'>
          <Text fontSize='sm'>
            <Trans i18nKey='dashboard.authentication.sapCxLinking.modalBody' />
          </Text>
          {error && (
            <Text color='form.errorText' fontWeight='bold'>
              {error}
            </Text>
          )}
          <HStack alignItems='flex-start' gap='3'>
            <MigrationCheckbox
              accessor='acceptedTerms'
              registerOptions={{ validate: (val) => val === true }}
              customLabelContent={
                <Text mt='-1' fontSize='xs'>
                  <Trans
                    i18nKey='dashboard.authentication.createAccountLabel'
                    components={{
                      TermsLink: (
                        <StyledLink
                          color='primaryButton.background'
                          textDecoration='underline'
                          to='/tos'
                        >
                          Link
                        </StyledLink>
                      ),
                    }}
                  />
                </Text>
              }
            />
          </HStack>
          <Text fontSize='xs'>
            <Trans
              i18nKey='dashboard.authentication.createAccountLabelPrivacy'
              components={{
                PrivacyLink: (
                  <StyledLink
                    color='primaryButton.background'
                    textDecoration='underline'
                    to='/privacy'
                  >
                    Link
                  </StyledLink>
                ),
              }}
            />
          </Text>
        </Stack>

        <Stack gap='6' w='full'>
          <TrackedButton
            fontSize='sm'
            isDisabled={!formMethods.formState.isValid}
            isLoading={isPending}
            type='submit'
            // ToDo: Tracking?
            // contentName='CloudAccount'
            // contentPiece='Registration'
            // contentTarget='NewAccount'
          >
            {t('dashboard.authentication.sapCxLinking.accept')}
          </TrackedButton>
        </Stack>
      </VStack>
    </FormProvider>
  );
};
